/**
 * R17 A01 (STATE-HIST) — `history/undo` + `history/redo`, extracted VERBATIM
 * from `editorStore.ts`'s `dispatch()` body (no behaviour change). Same
 * STORE-SPLIT lineage as `editorStoreStyleTables.ts` / `editorStoreImportActions.ts`
 * / `editorStoreImportFileActions.ts`.
 *
 * `editorStore.ts`'s `dispatch()` still OWNS the `command.type ===
 * "history/undo" | "history/redo"` routing (the discriminated-union check
 * itself is untouched); it delegates each branch's BODY to
 * `handleHistoryUndo` / `handleHistoryRedo` below, then returns.
 *
 * `set`/`get` ARE the store's `setState`/`getState` — zustand passes the SAME
 * function references into the `StateCreator` initializer, and `dispatch`
 * itself is a property of that same initializer object, so `get` is
 * `useEditorStore.getState` under a different local name. Every former
 * `useEditorStore.getState()` call site inside these bodies is therefore
 * byte-identical in behavior as `get()` (see `editorStoreImportActions.ts`'s
 * own header comment for this exact precedent/reasoning).
 *
 * The COLLAB-LOCK-COMMAND veto-aware history rationale below applies to ALL
 * SEVEN `history/*` command handlers — this file's two, plus
 * `editorStoreHistoryBatch.ts`'s begin/commit-batch and
 * `editorStoreHistoryReplay.ts`'s abort-batch/replay-forwards/replay-inverses.
 * Kept here (first in dispatch order) as the one-truth explanation; the other
 * two modules point back to it instead of repeating it.
 */
// ── COLLAB-LOCK-COMMAND — P1 (post-review, round 5): veto-aware history ────
// `dispatch()`'s `history/undo` / `history/redo` / `history/abort-batch` /
// `history/replay-forwards` / `history/replay-inverses` branches each
// recursively call `useEditorStore.getState().dispatch(...)` on the
// inverse/forward command with `__bypassHistory: true`, then unconditionally
// advance the `past`/`future` stacks — with NO way to know whether that
// recursive dispatch was rejected by the collab-lock EDIT veto (which lives
// deep inside the SAME `dispatch()` function, far below). Without this flag
// a locked entity's undo/redo would silently advance the history stacks (or
// clear an in-progress batch) even though the actual document mutation never
// happened — corrupting the past/future accounting (an entry popped from
// `past` and never truly reverted; a redo popped from `future` and never
// truly reapplied).
//
// Module-scoped rather than a `dispatch()` return value: `dispatch`'s public
// signature is `void` and is called from every UI/AI/collaboration site in
// the codebase — widening it to report a veto outcome would be a large,
// unjustified blast radius for this fix (ПРИОРИТЕТ #2 — ГРАФ ЗАВИСИМОСТЕЙ).
// GitHub Codex review (PR #534, round 2) P2 — the flag itself lives in the
// dedicated `collabLockDispatchVetoSignal.ts` leaf module (imported below as
// `wasLastDispatchCollabLockVetoed`), not as a private `let` here, so
// `editorStoreStyleTables.ts`'s `deleteLayer()`/`renameLayer()` can read it
// too without creating an import cycle — see that module's own header for
// the full reasoning. Reset to `false` as the FIRST statement of every
// `dispatch()` call (including recursive/nested ones, in `editorStore.ts`),
// so a caller that dispatches, then immediately reads the flag, sees the
// outcome of THAT call only — never a stale value left over from an
// unrelated earlier dispatch.
import {
  bumpForClasses,
  diffBumpClasses,
  type BumpClassSet,
  type DrawingDocument,
  type RevisionCounters,
} from "@dxf-ai-editor/drawing-model";
import {
  type DynamicBlockCommand,
  type EditorCommand,
  type EditorShellState,
} from "@dxf-ai-editor/contracts";
import { t } from "../i18n";
import { pushAuditEvent } from "./reducerHelpers";
import { computeRevisionStateDiff } from "./editorStoreRevisionDiff";
import { materializeDocumentSpacesConcretely } from "./spaceReadThrough";
import { wasLastDispatchCollabLockVetoed } from "./collabLockDispatchVetoSignal";

/**
 * The store surface the seven `history/*` handlers (this file's two, plus
 * `editorStoreHistoryBatch.ts`'s begin/commit-batch and
 * `editorStoreHistoryReplay.ts`'s abort-batch/replay-forwards/replay-inverses)
 * collectively read/write — a STRUCTURAL SUPERSET of every individual
 * handler's own usage, kept as ONE canonical declaration (not three
 * near-identical narrower ones — that duplication was the jscpd Этап-1
 * finding this consolidation resolves) and re-exported for the sibling
 * modules to import. Member types must stay IDENTICAL to `EditorStore`'s
 * (editorStore.ts) own declarations so `EditorStore` stays assignable to
 * this type for the `set`/`get` parameters. Declared here (not imported from
 * editorStore.ts) so the module has NO back-edge into `editorStore.ts` — the
 * deps graph stays acyclic.
 */
export interface HistoryOrchestrationHostStore {
  state: EditorShellState;
  geometryDocument: DrawingDocument;
  revisions: RevisionCounters;
  pendingBumpClasses: BumpClassSet | null;
  pendingDirectSetBumpClasses: BumpClassSet | null;
  pendingBatchBaseDoc: DrawingDocument | null;
  pendingBatchBaseState: EditorShellState | null;
  pendingBatchBaseAuditSequence: number | null;
  lastBatchClosedBy: "commit" | "abort" | "discard" | null;
  dispatch: (command: EditorCommand | DynamicBlockCommand) => void;
}

/** The zustand `setState` of the host store, narrowed to the history handlers' usage. */
export type HistoryOrchestrationSet = (
  updater: (
    store: HistoryOrchestrationHostStore,
  ) => HistoryOrchestrationHostStore | Partial<HistoryOrchestrationHostStore>,
) => void;

/** The zustand `getState` of the host store. */
export type HistoryOrchestrationGet = () => HistoryOrchestrationHostStore;

// ── Phase 4.1.c.2 — history/undo ────────────────────────────────
// Pop the top entry from `past`, re-apply the inverse command with
// __bypassHistory=true to avoid recursive recording, push popped
// entry to `future`. Selection is restored to `selectionBefore`.
export function handleHistoryUndo(
  command: Extract<
    EditorCommand | DynamicBlockCommand,
    { type: "history/undo" }
  >,
  set: HistoryOrchestrationSet,
  get: HistoryOrchestrationGet,
): void {
  const store = get();
  const { past } = store.state.history;
  if (past.length === 0) return; // nothing to undo
  const entry = past[past.length - 1]!;
  const inverseCmd = entry.inverse as EditorCommand;
  // ABB-S1 G1 (STRUCTURAL ROOT) — snapshot the REAL pre-apply state so the
  // bump is grounded in the ACTUAL applied delta (like the forward path), NOT
  // the command kind. The kind-classifier under/mis-bumped wrappers, runtime-
  // dependent commands, ATTDEF authoring, and nested replays; diffing the net
  // document+shell change classifies them all correctly in one move.
  //
  // PR #362 round-5 (F3 ROOT) — `store.geometryDocument` is a COMPACT-CACHE
  // read-through wrapper whose `space.entities` getter reads the LIVE cache.
  // The inverse dispatch below MUTATES that cache, so a wrapper captured here
  // would, when the diff later reads `.entities`, see the POST-mutation cache
  // — making prev⇔next look identical and DROPPING the geometry bump. Freeze
  // the base into CONCRETE entity arrays at capture time so the diff reflects
  // the true pre→post delta.
  const preApplyDoc = materializeDocumentSpacesConcretely(
    store.geometryDocument,
  );
  const preApplyState = store.state;
  // Apply the inverse via the bypass path (won't re-record).
  // Cast through unknown: __bypassHistory is an internal flag stripped
  // before routing; it is not part of any EditorCommand variant's schema.
  get().dispatch(
    { ...inverseCmd, __bypassHistory: true } as unknown as EditorCommand,
  );
  // COLLAB-LOCK-COMMAND — P1 (post-review, round 5): the recursive
  // inverse dispatch above may have been REJECTED by the fail-closed
  // collab-lock EDIT veto (another collaborator now holds a soft lock on
  // an entity/layer the inverse touches — e.g. it was unlocked when
  // originally applied but got locked by someone else since). When that
  // happens the document was NOT reverted, so popping `past`/pushing
  // `future` here would desync the history stacks from the actual
  // document state — the entry would look "undone" in the stacks while
  // its effect is still live. Leave BOTH stacks untouched (the entry
  // stays in `past` for a retry once the lock releases) and push a
  // distinct audit error instead of the normal undo record.
  if (wasLastDispatchCollabLockVetoed()) {
    set((s) => ({
      state: pushAuditEvent(
        s.state,
        command,
        t("collab.lock.undo_vetoed"),
        "error",
      ),
    }));
    return;
  }
  // Update history stacks + restore selection, then emit a CC-1c
  // history-traverse audit event (ТЗ §14). The reversed command's type +
  // the resulting head index are attached as typed params so the audit
  // log / telemetry record exactly what was undone and where the head now
  // sits. (The inverse dispatch above may also emit its own audit row;
  // this UNDO marker is the meta-action record on top.)
  set((s) => {
    const newPast = s.state.history.past.slice(0, -1);
    const reversedType = (entry.forward as { type?: string }).type ?? "unknown";
    const nextState: EditorShellState = {
      ...s.state,
      history: {
        ...s.state.history,
        past: newPast,
        future: [...s.state.history.future, entry],
      },
      selection: {
        ...s.state.selection,
        entityIds: [...entry.selectionBefore],
      },
    };
    // ABB-S1 G1 — DUAL-BUMP from the REAL net delta: pre-apply state vs the
    // post-apply state WITH the restored selection (`nextState` carries the
    // selection=selectionBefore restore + the post-apply doc in `s`). The
    // state-diff captures the document change (entity/table/context) AND the
    // selection restore in ONE classification — no kind-classifier, so a
    // wrapper / runtime-dependent / ATTDEF / nested-replay inverse all bump
    // by what they ACTUALLY changed.
    const undoDiff = computeRevisionStateDiff(
      preApplyDoc,
      s.geometryDocument,
      preApplyState,
      nextState,
    );
    return {
      state: pushAuditEvent(
        nextState,
        command,
        t("audit.history_undo", { command: reversedType }),
        "info",
        { reversedCommandType: reversedType, headIndex: newPast.length },
      ),
      revisions: bumpForClasses(s.revisions, diffBumpClasses(undoDiff)),
    };
  });
}

// ── Phase 4.1.c.2 — history/redo ────────────────────────────────
// Pop the top entry from `future`, re-apply the forward command with
// __bypassHistory=true, push back to `past`. Restore selectionAfter.
export function handleHistoryRedo(
  command: Extract<
    EditorCommand | DynamicBlockCommand,
    { type: "history/redo" }
  >,
  set: HistoryOrchestrationSet,
  get: HistoryOrchestrationGet,
): void {
  const store = get();
  const { future } = store.state.history;
  if (future.length === 0) return; // nothing to redo
  const entry = future[future.length - 1]!;
  const forwardCmd = entry.forward as EditorCommand;
  // ABB-S1 G1 — snapshot the REAL pre-apply state (see the undo handler): the
  // redo bump is the diff of the net applied delta, not the forward kind.
  // PR #362 round-5 (F3 ROOT) — freeze to CONCRETE entity arrays (same reason
  // as the undo handler: the forward dispatch mutates the live cache the
  // read-through wrapper reads through, which would null the geometry diff).
  const preApplyDoc = materializeDocumentSpacesConcretely(
    store.geometryDocument,
  );
  const preApplyState = store.state;
  get().dispatch(
    { ...forwardCmd, __bypassHistory: true } as unknown as EditorCommand,
  );
  // COLLAB-LOCK-COMMAND — P1 (post-review, round 5): mirror the UNDO
  // handler's veto check. The recursive forward dispatch above may have
  // been REJECTED by the fail-closed collab-lock EDIT veto (the entity/
  // layer got locked by another collaborator since it was undone). Leave
  // BOTH stacks untouched (the entry stays in `future` for a retry once
  // the lock releases) and push a distinct audit error.
  if (wasLastDispatchCollabLockVetoed()) {
    set((s) => ({
      state: pushAuditEvent(
        s.state,
        command,
        t("collab.lock.redo_vetoed"),
        "error",
      ),
    }));
    return;
  }
  // CC-1c — mirror the UNDO handler: emit a typed REDO history-traverse
  // audit event recording the re-applied command type + the new head index.
  set((s) => {
    const newPast = [...s.state.history.past, entry];
    const reappliedType = (entry.forward as { type?: string }).type ?? "unknown";
    const nextState: EditorShellState = {
      ...s.state,
      history: {
        ...s.state.history,
        past: newPast,
        future: s.state.history.future.slice(0, -1),
      },
      selection: {
        ...s.state.selection,
        entityIds: [...entry.selectionAfter],
      },
    };
    // ABB-S1 G1 — DUAL-BUMP from the REAL net delta (mirror of UNDO): pre-
    // apply state vs the post-apply state WITH the restored selectionAfter.
    const redoDiff = computeRevisionStateDiff(
      preApplyDoc,
      s.geometryDocument,
      preApplyState,
      nextState,
    );
    return {
      state: pushAuditEvent(
        nextState,
        command,
        t("audit.history_redo", { command: reappliedType }),
        "info",
        { reappliedCommandType: reappliedType, headIndex: newPast.length },
      ),
      revisions: bumpForClasses(s.revisions, diffBumpClasses(redoDiff)),
    };
  });
}
